import {inject, Injectable} from '@angular/core';
import {CoreService} from '../../../core/service';
import {deleteField, doc, Firestore, updateDoc} from '@angular/fire/firestore';
import {
  Auth,
  authState,
  browserLocalPersistence,
  browserSessionPersistence,
  EmailAuthProvider,
  inMemoryPersistence,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from '@angular/fire/auth';
import {Store} from '@ngrx/store';
import * as NotificationActions from '../../../notifications/store/notification.actions';
import {IFirebaseUser, IFirebaseUserCredential, IUser} from '../../models';
import {RuleHumanIdEnum} from '../../models/rule-info.enum';
import {from, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {loginUserSuccess} from '../../user-store/user.actions';
import {FirebaseError} from '@angular/fire/app';
import {FireAuthService} from '../../../../shared-utilities/services-old/fire-auth.service';
import {IGlobalError} from '../../../../shared/shared-components/components/global-error-display/models/global-error';
import {ErrorName} from '../../../../shared/shared-components/components/global-error-display/models/global-error.enum';
import {AuthPersistenceType} from '../../models/auth-persistence.enum';

@Injectable({
  providedIn: 'root',
})
export class UserService extends CoreService {

  #tempPassword: string | null = null;

  constructor(
    private readonly auth: Auth,
    private readonly replaceAuth: FireAuthService,
    private readonly store: Store,
    private readonly fireStore: Firestore,
  ) {
    super(inject(Firestore));
    this.listenToAuthState();
  }

  setTempPassword(password: string): void {
    this.#tempPassword = password;
  }

  getTempPassword(): string | null {
    const temp = this.#tempPassword;
    this.#tempPassword = null;
    return temp;
  }

  setAuthPersistence(persistenceType: AuthPersistenceType = 'session'): Observable<boolean> {
    let persistence: typeof browserLocalPersistence | typeof browserSessionPersistence | typeof inMemoryPersistence;
    switch (persistenceType.toUpperCase()) {
      case 'LOCAL':
        persistence = browserLocalPersistence;
        break;
      case 'SESSION':
        persistence = browserSessionPersistence;
        break;
      case 'NONE':
        persistence = inMemoryPersistence;
        break;
      default:
        return of(false);
    }
    return from(setPersistence(this.auth, persistence)).pipe(
      map((): boolean => true),
      catchError((error: IGlobalError): Observable<boolean> => {
        this.store.dispatch(
          NotificationActions.setGlobalErrorOccurrence({
            error: {
              ...error,
              action: JSON.stringify('setPersistence(auth, persistenceType)'),
              actionType: 'setPersistence',
              message: `Error setting persistence ${persistenceType} for Auth.`,
              originalError: error,
            },
          }),
        );
        return of(false);
      }),
    );
  }

  /**
   * Signs in with email and the temporarily stored password.
   * @param userEmail - The user's email address
   * @returns The authenticated user, or null if email is empty
   * @throws IGlobalError for network or authentication failures
   */
  async logIn(userEmail: string): Promise<IUser | null> {
    const tempPassword: string | null = this.getTempPassword();

    if (!userEmail) {
      return null;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const offlineError: IGlobalError = {
        name: ErrorName.NetworkError,
        message: 'No internet connection. Check your connection and try again.',
      } as IGlobalError;
      throw offlineError;
    }

    try {
      const userCredential: IFirebaseUserCredential = await signInWithEmailAndPassword(
        this.auth,
        userEmail,
        tempPassword,
      );
      return {
        id: userCredential.user.email,
        userName: userCredential.user.email,
      };
    } catch (error) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/network-request-failed':
            const networkError: IGlobalError = {
              name: ErrorName.NetworkError,
              message: 'No internet connection. Check your connection and try again.',
              originalError: error,
            } as IGlobalError;
            throw networkError;
          case 'auth/invalid-credential':
          case 'auth/wrong-password':
          case 'auth/user-not-found':
          case 'auth/invalid-email':
            const invalidCredentialsError: IGlobalError = {
              name: ErrorName.AuthenticationError,
              message: 'Login failed: invalid login credentials.',
              originalError: error,
            } as IGlobalError;
            throw invalidCredentialsError;
          case 'auth/too-many-requests':
            const tooManyRequestsError: IGlobalError = {
              name: ErrorName.AuthenticationError,
              message: 'Too many failed login attempts. Please try again later.',
              originalError: error,
            } as IGlobalError;
            throw tooManyRequestsError;
          case 'auth/user-disabled':
            const userDisabledError: IGlobalError = {
              name: ErrorName.AuthenticationError,
              message: 'This user account has been disabled.',
              originalError: error,
            } as IGlobalError;
            throw userDisabledError;
          default:
            const unknownAuthError: IGlobalError = {
              name: ErrorName.AuthenticationError,
              message: 'Login failed. Please try again.',
              originalError: error,
            } as IGlobalError;
            throw unknownAuthError;
        }
      }

      const genericError: IGlobalError = {
        name: ErrorName.whyGlobalError,
        message: 'An unexpected error occurred while logging in.',
        originalError: error,
      } as IGlobalError;
      throw genericError;
    }
  }

  async logOut(): Promise<void> {
    await signOut(this.auth);
  }

  async resetUserPassword(email: string, userId?: string, storeId?: string): Promise<void> {
    if (!userId || userId === email) {
      await this.sendResetEmail(email);
      return;
    }

    if (storeId && this.replaceAuth.hasAccess(storeId, {ruleID: RuleHumanIdEnum['a.']})) {
      await this.sendResetEmail(email);
      return;
    }

    const permissionError: IGlobalError = {
      name: ErrorName.PermissionError,
      message: `You do not have permission to request a password reset for ${email}.`,
    } as IGlobalError;
    throw permissionError;
  }

  /**
   * Changes the password for the currently authenticated user
   * @param currentPassword - The user's current password for reauthentication
   * @param newPassword - The new password to set
   * @returns Promise that resolves when password is changed successfully
   * @throws IGlobalError if reauthentication fails or password update fails
   */
  async changeUserPassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = this.auth.currentUser;

    if (!user || !user.email) {
      const authError: IGlobalError = {
        name: ErrorName.AuthenticationError,
        message: 'No authenticated user found. Please log in again.',
      } as IGlobalError;
      throw authError;
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
    } catch (error) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            const wrongPasswordError: IGlobalError = {
              name: ErrorName.AuthenticationError,
              message: 'Current password is incorrect. Please try again.',
              originalError: error,
            } as IGlobalError;
            throw wrongPasswordError;
          case 'auth/weak-password':
            const weakPasswordError: IGlobalError = {
              name: ErrorName.InvalidInputError,
              message: 'New password is too weak. Please choose a stronger password.',
              originalError: error,
            } as IGlobalError;
            throw weakPasswordError;
          case 'auth/requires-recent-login':
            const recentLoginError: IGlobalError = {
              name: ErrorName.AuthenticationError,
              message: 'This operation requires recent authentication. Please log out and log in again.',
              originalError: error,
            } as IGlobalError;
            throw recentLoginError;
          default:
            const unknownError: IGlobalError = {
              name: ErrorName.whyGlobalError,
              message: 'Failed to change password. Please try again.',
              originalError: error,
            } as IGlobalError;
            throw unknownError;
        }
      }

      const genericError: IGlobalError = {
        name: ErrorName.whyGlobalError,
        message: 'An unexpected error occurred while changing password.',
        originalError: error,
      } as IGlobalError;
      throw genericError;
    }
  }

  private listenToAuthState(): void {
    authState(this.auth).subscribe((user: IFirebaseUser): void => {
      if (user?.email) {
        const authenticatedUser: IUser = {
          id: user.email,
          userName: user.email,
        };
        this.store.dispatch(loginUserSuccess({user: authenticatedUser}));
      }
    });
  }

  private async sendResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      if (error instanceof FirebaseError && error.code === 'auth/user-not-found') {
        const userNotFoundError: IGlobalError = {
          name: ErrorName.AuthUserNotFoundError,
          message: `User with email ${email} not found.`,
          originalError: error,
        } as IGlobalError;
        throw userNotFoundError;
      }

      const resetError: IGlobalError = {
        name: ErrorName.PasswordResetError,
        message: 'Failed to send password reset email.',
        originalError: error,
      } as IGlobalError;
      throw resetError;
    }
  }


  deleteUserAccess(path: string): Observable<void> {
    const ref: ReturnType<typeof doc> = doc(this.fireStore, path);
    return from(
      updateDoc(ref, {
        adminAccessStores: deleteField(),
        features: deleteField(),
        status: deleteField(),
        storeList: deleteField(),
        stores: deleteField(),
        temp: deleteField(),
      }),
    );
  }

}
